import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import {
  Spinner,
  SpinnerColorScheme,
} from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";

import { Logger } from "@/apis/Logger";
import { publishVpnStatusCheckMetric } from "@/apis/Metrics";
import { submitUserConsentV2 } from "@/apis/UserConsentsV2Helper";
import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import VpnMacConnected from "@/assets/images/vpnMacConnected.png";
import VpnMacEnterPin from "@/assets/images/vpnMacEnterPin.png";
import VpnMacEnterUsername from "@/assets/images/vpnMacEnterUsername.png";
import VpnMacSelectGroup from "@/assets/images/vpnMacSelectGroup.png";
import VpnOrcaCorpConfirmation from "@/assets/images/vpnOrcaCorpConfirmation.png";
import VpnOrcaCorpConnect from "@/assets/images/vpnOrcaCorpConnect.png";
import VpnOrcaCorpPin from "@/assets/images/vpnOrcaCorpPin.png";
import VpnOrcaCorpSelectGroup from "@/assets/images/vpnOrcaCorpSelectGroup.png";
import VpnOrcaCorpSpotlight from "@/assets/images/vpnOrcaCorpSpotlight.png";
import VpnOrcaCorpSuccess from "@/assets/images/vpnOrcaCorpSuccess.png";
import VpnOrcaCorpTouchKey from "@/assets/images/vpnOrcaCorpTouchKey.png";
import VpnOrcaCorpUsername from "@/assets/images/vpnOrcaCorpUsername.png";
import VpnPressWinKey from "@/assets/images/vpnPressWinKey.png";
import VpnReadyToConnect from "@/assets/images/vpnReadyToConnect.png";
import VpnWinEnterPass from "@/assets/images/vpnWinEnterPass.png";
import VpnWinLaunch from "@/assets/images/vpnWinLaunch.png";
import VpnWinSecureConnect from "@/assets/images/vpnWinSecureConnect.png";
import VpnWinSelectGroup from "@/assets/images/vpnWinSelectGroup.png";
import {
  AIAutomationOverlay,
  AIOptInDisclosure,
} from "@/components/AIAutomation";
import ImagesRow from "@/components/Commons/ImagesRow";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { StepNextGuardContext } from "@/components/Commons/StepContainer";
import {
  MANUAL_FALLBACK_THRESHOLD,
  STEP_ID,
  StepsList,
} from "@/components/constants";
import { getAgentCoreUrl } from "@/helpers/agentcore/agentcore-url-helper";
import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";
import { isElectronApp } from "@/helpers/electron-helper";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import {
  checkVpnConnectionStatus,
  isVpnStatusCheckAvailable,
} from "@/helpers/vpn-status-helper";
import { AIWorkflowMetricsHelper } from "@/helpers/workflow-metrics-helper";
import { useInvokeAgent } from "@/hooks/useInvokeAgent";
import { useHiringRegion } from "@/hooks/useRegion";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { useFeatureFlag } from "@/stores/contexts/FeatureFlagsContext";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

const FONT_WEIGHT = "regular";

/**
 * Version of the AI automation consent terms.
 * Bump this when the consent disclosure language changes materially.
 */
const AI_CONSENT_TERMS_VERSION = "2.0";

const GLOBAL_EMPLOYEE_PRIVACY_NOTICE_URL =
  "https://inside.hr.amazon.dev/content/inside/us/en/employment/policies-global/employee-privacy-notice/global.html";

interface VpnOptInProps {
  onStartAutomation: () => void;
  onDeclineAutomation: () => void;
  hasUserPreviouslyConsented: boolean;
}

const VpnOptIn = ({
  onStartAutomation,
  onDeclineAutomation,
  hasUserPreviouslyConsented,
}: VpnOptInProps): JSX.Element => (
  <AIOptInDisclosure
    autoVpnSetupConsentTextI18nId="connectVpn-ai-disclosure-auto-vpn-setup-consent-text"
    screenshotCollectionConsentTextI18nId="connectVpn-ai-disclosure-screenshot-collection-consent-text"
    declineButtonTextI18nId="connectVpn-ai-disclosure-decline-button"
    startButtonTextI18nId="connectVpn-ai-disclosure-start-button"
    onStartAutomation={onStartAutomation}
    onDeclineAutomation={onDeclineAutomation}
    agentType="vpn"
    initialAutoVpnSetupConsent={hasUserPreviouslyConsented}
    initialScreenshotCollectionConsent={hasUserPreviouslyConsented}
  >
    <Card className="ai-intro-banner">
      <Col alignItems="flex-start" justifyContent="center" gridGap="S100">
        {/* TODO: Move inline color to stylesheet after UX review - https://taskei.amazon.dev/tasks/NHE-5673 */}
        <Text fontWeight="bold" style={{ color: "#232F3E" }}>
          <FormattedMessage id="connectVpn-ai-disclosure-title" />
        </Text>
      </Col>
    </Card>

    <Text fontWeight={FONT_WEIGHT}>
      <FormattedMessage id="connectVpn-ai-disclosure-description" />
    </Text>

    <Text fontWeight={FONT_WEIGHT}>
      <ul className="ai-how-it-works-list">
        <li>
          <FormattedMessage id="connectVpn-ai-disclosure-no-adverse-consequences" />
        </li>
        <li>
          <FormattedMessage
            id="connectVpn-ai-disclosure-personal-data-collection"
            values={{
              privacyNoticeLink: (chunks: React.ReactNode[]) => (
                <Link
                  href={GLOBAL_EMPLOYEE_PRIVACY_NOTICE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </li>
      </ul>
    </Text>

    <Col gridGap="S300">
      <Text fontWeight="bold">
        <FormattedMessage id="connectVpn-ai-disclosure-how-it-works-title" />
      </Text>
      <Text fontWeight={FONT_WEIGHT}>
        <ul className="ai-how-it-works-list">
          <li>
            <FormattedMessage id="connectVpn-ai-disclosure-how-it-works-step1" />
          </li>
          <li>
            <FormattedMessage id="connectVpn-ai-disclosure-how-it-works-step2" />
          </li>
        </ul>
      </Text>
    </Col>
  </AIOptInDisclosure>
);

const VpnAutomationInProgress = ({
  onStopAutomation,
}: {
  onStopAutomation: () => void;
}): JSX.Element => <AIAutomationOverlay onStopAutomation={onStopAutomation} />;

const VpnAutomationSuccess = (): JSX.Element => (
  <MessageBanner
    dataTestId="vpnAutomationSuccessBanner"
    type={MessageBannerType.Success}
  >
    <Text>
      <FormattedMessage id="connectVpn-ai-automation-success-message" />
    </Text>
  </MessageBanner>
);

const VpnAutomationError = ({
  onBackToSetup,
  onManualFallback,
  failureCount,
}: {
  onBackToSetup: () => void;
  onManualFallback: () => void;
  failureCount: number;
}): JSX.Element => (
  <AIAutomationOverlay
    onStopAutomation={onBackToSetup}
    onManualFallback={onManualFallback}
    isFailed={true}
    failureCount={failureCount}
  />
);

const VpnManualSetup = (): JSX.Element => (
  <>
    <StepBodyStyledText>
      <Text fontWeight={FONT_WEIGHT} dataTestId="connectVpnIntroText">
        <FormattedMessage id="connectVpn-expander-body-intro" />
      </Text>
    </StepBodyStyledText>

    <Hr color="neutral20" size="narrow" />

    <StepBodyStyledText>
      <Text fontWeight={FONT_WEIGHT}>
        <ol className="stepLists">
          <VpnStepsList />
        </ol>
      </Text>
    </StepBodyStyledText>
  </>
);

/**
 * The steps of connecting to VPN on a macOS machine.
 */
const MAC_OS_VPN_STEPS: StepsList[] = [
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step1`,
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step2`,
    image: {
      src: VpnReadyToConnect,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step2-altText`,
      widthPx: 438,
      heightPx: 215,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step3`,
    image: {
      src: VpnMacSelectGroup,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step3-altText`,
      widthPx: 639,
      heightPx: 322,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step4`,
    image: {
      src: VpnMacEnterUsername,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step4-altText`,
      widthPx: 515,
      heightPx: 190,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step5`,
    image: {
      src: VpnMacEnterPin,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step5-altText`,
      widthPx: 635,
      heightPx: 228,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step6`,
    image: {
      src: VpnMacConnected,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.MAC}-step6-altText`,
      widthPx: 261,
      heightPx: 313,
    },
  },
];

/**
 * macOS VPN steps for the Orca-Corp-VPN flow (feature-gated).
 */
const ORCA_CORP_MAC_VPN_STEPS: StepsList[] = [
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step1`,
    image: {
      src: VpnOrcaCorpSpotlight,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step1-altText`,
      widthPx: 560,
      heightPx: 331,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step2`,
    image: {
      src: VpnOrcaCorpConnect,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step2-altText`,
      widthPx: 560,
      heightPx: 310,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step3`,
    image: {
      src: VpnOrcaCorpSelectGroup,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step3-altText`,
      widthPx: 560,
      heightPx: 251,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step4`,
    image: {
      src: VpnOrcaCorpUsername,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step4-altText`,
      widthPx: 400,
      heightPx: 333,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step5`,
    image: {
      src: VpnOrcaCorpPin,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step5-altText`,
      widthPx: 400,
      heightPx: 311,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step6`,
    image: {
      src: VpnOrcaCorpTouchKey,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step6-altText`,
      widthPx: 400,
      heightPx: 297,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step7`,
    image: {
      src: VpnOrcaCorpConfirmation,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step7-altText`,
      widthPx: 420,
      heightPx: 403,
    },
  },
  {
    stepLocaleId: `connectOrcaCorpVpn-expander-body-${OPERATING_SYSTEM.MAC}-step8`,
    image: {
      src: VpnOrcaCorpSuccess,
      altTextLocaleId: `connectOrcaCorpVpn-image-${OPERATING_SYSTEM.MAC}-step8-altText`,
      widthPx: 600,
      heightPx: 130,
    },
  },
];

/**
 * The steps of connecting to VPN on a Windows machine.
 */
const WINDOWS_VPN_STEPS: StepsList[] = [
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step1`,
    image: {
      src: VpnPressWinKey,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step1-altText`,
      widthPx: 317,
      heightPx: 319,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step2`,
    image: {
      src: VpnWinLaunch,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.WINDOWS}-step2-altText`,
      widthPx: 360,
      heightPx: 119,
    },
    children: (
      <Col padding={{ top: "S300" }}>
        <MessageBanner
          dataTestId="connectVpnStep5Warning"
          type={MessageBannerType.Warning}
        >
          <Text>
            <FormattedMessage
              id={`connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step2-note`}
              values={{
                ciscoText: "Cisco Secure Client",
              }}
            />
          </Text>
        </MessageBanner>
      </Col>
    ),
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step3`,
    image: {
      src: VpnWinSecureConnect,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step3-altText`,
      widthPx: 372,
      heightPx: 253,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step4`,
    image: {
      src: VpnWinSelectGroup,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step4-altText`,
      widthPx: 372,
      heightPx: 253,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step5`,
  },
  {
    stepLocaleId: `connectVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step6`,
    image: {
      src: VpnWinEnterPass,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step6-altText`,
      widthPx: 364,
      heightPx: 224,
    },
  },
  {
    stepLocaleId: `connectVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step7`,
  },
];

/**
 * The detail steps needed to connect to the VPN.
 */
const VpnStepsList = (): JSX.Element => {
  const isOrcaCorpVpn = useFeatureFlag("macVpnOrcaCorp");
  const macSteps = isOrcaCorpVpn ? ORCA_CORP_MAC_VPN_STEPS : MAC_OS_VPN_STEPS;
  const stepsList =
    getOS() === OPERATING_SYSTEM.MAC ? macSteps : WINDOWS_VPN_STEPS;

  return (
    <>
      {stepsList.map((step, index) => (
        <Text
          className="stepList-text"
          key={step.stepLocaleId}
          fontWeight={FONT_WEIGHT}
          dataTestId={`connectVpnStep${index + 1}`}
        >
          <li>
            <FormattedMessage
              id={step.stepLocaleId}
              values={{
                ciscoText: "Cisco Secure Client",
                groupText: "Group",
                orcaText: "orca-Amazon-Onboarding",
                orcaCorpText: "Orca-Corp-VPN",
                usernameText: "Username",
                keyPinText: "Security key PIN + Press security key",
                passwordText: "Password",
                spotlightIconText: "Spotlight icon",
                searchTermText: "secure",
                connectText: "Connect",
                signInText: "Sign in",
                pinFieldText: "PIN",
                nextText: "Next",
                okText: "OK",
              }}
            />
            {step.image ? (
              <Col padding={{ top: "S300" }}>
                <ImagesRow images={[step.image]} rowPadding="S400" />
              </Col>
            ) : null}
            {step.children}
          </li>
        </Text>
      ))}
    </>
  );
};

/**
 * A step to help NHs to connect to the Amazon network through VPN.
 */
export default function ConnectVpn(): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const systemOS = getOS();
  const hiringRegion = useHiringRegion();
  const isAiAutomationEnabled = useFeatureFlag("aiAutomation");
  const isInvokeAgentAvailable =
    typeof window.electronAPI?.invokeAgent === "function";
  const isAiAutomationAvailable =
    isAiAutomationEnabled && isInvokeAgentAvailable;

  const [showAIOptIn, setShowAIOptIn] = useState<boolean>(
    isElectronApp() && isAiAutomationAvailable
  );
  const [aiAutomationInProgress, setAiAutomationInProgress] =
    useState<boolean>(false);
  const [hasUserPreviouslyConsented, setHasUserPreviouslyConsented] =
    useState<boolean>(false);
  const [automationCompleted, setAutomationCompleted] =
    useState<boolean>(false);
  const [automationFailed, setAutomationFailed] = useState<boolean>(false);
  const [failureCount, setFailureCount] = useState<number>(0);
  const workflowStartTimeRef = useRef<number>(0);
  const progressUpdateCountRef = useRef<number>(0);
  const isCancelledRef = useRef<boolean>(false);

  /**
   * On-demand VPN status check state (NHE-6186). The check runs when the
   * user clicks Next in the manual flow (via the StepContainer next-guard)
   * and is advisory only — it never hard-blocks progression.
   */
  const setNextGuard = useContext(StepNextGuardContext);
  const [vpnCheckWarning, setVpnCheckWarning] = useState<"disconnected" | null>(
    null
  );
  const [vpnCheckRetryFailed, setVpnCheckRetryFailed] =
    useState<boolean>(false);
  const [vpnCheckPassedAfterRetry, setVpnCheckPassedAfterRetry] =
    useState<boolean>(false);
  /**
   * Ref mirror of vpnCheckPassedAfterRetry for the stable guard callback:
   * a successful retry already confirmed the connection, so the following
   * Next click advances immediately instead of re-running the CLI check.
   */
  const vpnCheckPassedAfterRetryRef = useRef<boolean>(false);
  const [vpnCheckInFlight, setVpnCheckInFlight] = useState<boolean>(false);

  const markVpnCheckPassedAfterRetry = useCallback((passed: boolean): void => {
    vpnCheckPassedAfterRetryRef.current = passed;
    setVpnCheckPassedAfterRetry(passed);
  }, []);

  const initialPrompt = `[hiring_region:${
    hiringRegion || "US"
  }] I would like to setup my VPN connection with Cisco Secure Client VPN on a ${systemOS} OS machine.`;

  const agentCoreUrl = useMemo(
    () => getAgentCoreUrl(hiringRegion),
    [hiringRegion]
  );

  const invokeVpnAgent = useInvokeAgent(
    // TODO(NHE-5682): drop the [hiring_region:...] prefix once the widget
    // routes EU users directly to the EU runtime via agentCoreUrl.
    // https://taskei.amazon.dev/tasks/NHE-5682
    initialPrompt,
    "vpn",
    agentCoreUrl
  );

  /**
   * Whether the manual setup flow is what the user currently sees. The
   * VPN status check only guards this flow — the AI automation flow does
   * its own verification before reporting success.
   */
  const isManualFlowActive =
    !isAiAutomationAvailable ||
    (!showAIOptIn &&
      !aiAutomationInProgress &&
      !automationCompleted &&
      !automationFailed);

  /**
   * Next-guard for the manual flow: checks the VPN connection once when the
   * user clicks Next. `connected` and `unknown` proceed (`unknown` fails
   * open — we can't assert anything, so we don't nag); `disconnected` and
   * `not-installed` stay on the step and surface the warning, which hides
   * Next and offers "Check again" / "Continue anyway" instead. Skipped
   * entirely when the preload bridge is unavailable (browser or older
   * desktop app versions).
   */
  const vpnStatusNextGuard = useCallback(async (): Promise<boolean> => {
    if (!isVpnStatusCheckAvailable()) {
      return true;
    }
    if (vpnCheckPassedAfterRetryRef.current) {
      // Consume the successful-retry result exactly once. The immediate Next
      // advances without a redundant CLI check, while a later visit/check
      // must verify the current connection state again.
      markVpnCheckPassedAfterRetry(false);
      return true;
    }
    setVpnCheckInFlight(true);
    const vpnState = await checkVpnConnectionStatus();
    setVpnCheckInFlight(false);
    if (vpnState === "connected") {
      publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckConnected);
      return true;
    }
    if (vpnState !== "disconnected") {
      // "unknown" (uninterpretable CLI output) and "not-installed" (no CLI
      // found) both mean the check itself is unreliable on this machine —
      // Day-1 laptops ship with the client preinstalled, so a missing CLI
      // points at path drift, not the user. Fail open rather than risk a
      // false warning; the metric still records it for the team.
      publishVpnStatusCheckMetric(
        vpnState === "unknown"
          ? Day1EventAction.VpnStatusCheckUnknown
          : Day1EventAction.VpnStatusCheckNotInstalled
      );
      return true;
    }
    publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckDisconnected);
    markVpnCheckPassedAfterRetry(false);
    setVpnCheckWarning(vpnState);
    return false;
  }, [markVpnCheckPassedAfterRetry]);

  /**
   * Register the guard while the manual flow is visible; clear it (and any
   * stale warning) when the user is in the AI automation flow or the step
   * unmounts.
   */
  useEffect(() => {
    if (isManualFlowActive) {
      setNextGuard(vpnStatusNextGuard);
    } else {
      setNextGuard(null);
      setVpnCheckWarning(null);
      setVpnCheckRetryFailed(false);
      markVpnCheckPassedAfterRetry(false);
    }
    return () => setNextGuard(null);
  }, [
    isManualFlowActive,
    setNextGuard,
    vpnStatusNextGuard,
    markVpnCheckPassedAfterRetry,
  ]);

  /**
   * Re-run the check from the warning banner. On `connected`, swap the
   * warning for a success banner (bringing Next back) and let the user
   * click Next (the guard will pass). On `unknown`, clear the warning
   * silently (the guard fails open). Otherwise keep the warning and
   * surface the chat-support hint.
   */
  const handleVpnCheckRetry = async (): Promise<void> => {
    publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckRetry);
    setVpnCheckInFlight(true);
    const vpnState = await checkVpnConnectionStatus();
    setVpnCheckInFlight(false);
    if (vpnState === "connected") {
      publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckConnected, {
        retry: true,
      });
      setVpnCheckWarning(null);
      setVpnCheckRetryFailed(false);
      markVpnCheckPassedAfterRetry(true);
    } else if (vpnState === "disconnected") {
      publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckDisconnected, {
        retry: true,
      });
      setVpnCheckWarning(vpnState);
      setVpnCheckRetryFailed(true);
    } else {
      // Inconclusive: "unknown" covers transitional CLI states (e.g.
      // "Connecting" while Cisco establishes the tunnel) and
      // "not-installed" covers a missing CLI (path drift). Keep the
      // warning up rather than silently dismissing it — the user's next
      // retry a moment later gets the real answer.
      publishVpnStatusCheckMetric(
        vpnState === "unknown"
          ? Day1EventAction.VpnStatusCheckUnknown
          : Day1EventAction.VpnStatusCheckNotInstalled,
        { retry: true }
      );
    }
  };

  /**
   * Open the IT Support chat flyout from the warning (shown after a failed
   * retry). Mirrors the security key timeout's "I need help" button.
   */
  const handleVpnCheckChatSupport = (): void => {
    publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckChatSupport, {
      warning: vpnCheckWarning,
    });
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_LINK,
      chatSupportRefLink: "ezo-inline",
    });
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_FLYOUT_VISIBLE,
      chatSupportFlyoutVisible: true,
    });
  };

  /**
   * The check is advisory: let the user proceed past the warning. Advances
   * the step directly (same pattern the security key screens use); the
   * step's own Next button is hidden while the warning shows.
   */
  const handleVpnCheckContinueAnyway = (): void => {
    // This path advances outside StepContainer.handleNext, so preserve the
    // baseline step-funnel metric that every normal Next exit emits.
    trackStructEvent({
      category: Day1EventCategory.ConnectToVpn,
      action: Day1EventAction.Continue,
    });
    publishVpnStatusCheckMetric(Day1EventAction.VpnStatusCheckContinueAnyway, {
      warning: vpnCheckWarning,
    });
    setVpnCheckWarning(null);
    setVpnCheckRetryFailed(false);
    dispatch({
      type: ActionType.NEXT_STEP,
      stepId: STEP_ID.CONNECT_VPN,
    });
    try {
      window.electronAPI.closeRightView();
    } catch (error) {
      // Ignore since the Electron API is available on the desktop app only
    }
  };

  const handleStartAutomation = () => {
    setShowAIOptIn(false);
    setAiAutomationInProgress(true);
    setAutomationCompleted(false);
    setHasUserPreviouslyConsented(true);
    workflowStartTimeRef.current = Date.now();
    progressUpdateCountRef.current = 0;
    isCancelledRef.current = false;
    void submitUserConsentV2(
      "AI_SCREENSHOT_AUTOMATION",
      true,
      AI_CONSENT_TERMS_VERSION
    );
    AIWorkflowMetricsHelper.emitWorkflowStarted("vpn");
    void invokeVpnAgent();
  };

  // Listen for automation completion, failure, timeout, and cancellation.
  useEffect(() => {
    const cleanup = window.electronAPI?.onAgentActionProgress?.((result) => {
      // Suppress trailing progress event after user-Stop — already handled.
      if (isCancelledRef.current) {
        return;
      }

      if (!result.success) {
        AIWorkflowMetricsHelper.emitWorkflowMetrics(result, "vpn", {
          iterations: progressUpdateCountRef.current,
          duration: Date.now() - workflowStartTimeRef.current,
        });
        setAiAutomationInProgress(false);
        setAutomationFailed(true);
        setFailureCount((prev) => prev + 1);
        return;
      }

      const response = result.response;
      if (!response) {
        return;
      }

      // Electron currently maps nav/reload/close cancels to FAILED + "Workflow
      // cancelled"; normalize so they don't count as agent failures.
      // TODO: drop once Electron emits state: "CANCELLED" directly.
      if (
        response.state === "FAILED" &&
        response.message === "Workflow cancelled"
      ) {
        response.state = "CANCELLED";
      }

      progressUpdateCountRef.current += 1;

      if (AIAgentSessionHelper.isTerminalState(response)) {
        AIWorkflowMetricsHelper.emitWorkflowMetrics(result, "vpn", {
          iterations: progressUpdateCountRef.current,
          duration: Date.now() - workflowStartTimeRef.current,
        });
      }

      if (response.state === "COMPLETED") {
        setAiAutomationInProgress(false);
        setAutomationCompleted(true);
        setAutomationFailed(false);
        setFailureCount(0);
      } else if (response.state === "FAILED" || response.state === "TIMEOUT") {
        setAiAutomationInProgress(false);
        setAutomationCompleted(false);
        setAutomationFailed(true);
        setFailureCount((prev) => prev + 1);
      } else if (response.state === "CANCELLED") {
        setAiAutomationInProgress(false);
        setShowAIOptIn(true);
      }
    });
    return () => cleanup?.();
  }, []);

  const handleDeclineAutomation = () => {
    if (failureCount >= MANUAL_FALLBACK_THRESHOLD) {
      AIWorkflowMetricsHelper.emitManualFallback("vpn", failureCount);
    }
    setShowAIOptIn(false);
    setAutomationFailed(false);
    setFailureCount(0);
  };

  const handleStopAutomation = () => {
    isCancelledRef.current = true;
    if (window.electronAPI?.cancelAgentWorkflow) {
      window.electronAPI.cancelAgentWorkflow().catch((error: Error) => {
        void Logger.errorErrorLike("Failed to cancel workflow:", error);
      });
    }
    AIWorkflowMetricsHelper.emitWorkflowCancelled(
      "vpn",
      Date.now() - workflowStartTimeRef.current
    );
    setAiAutomationInProgress(false);
    setShowAIOptIn(true);
  };

  const handleBackToSetup = () => {
    AIWorkflowMetricsHelper.emitBackToSetup("vpn", failureCount);
    setAutomationFailed(false);
    setShowAIOptIn(true);
  };

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    updateOnboardingStep("D1_VPN");

    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-connect-to-vpn",
    });
  }, []);

  /**
   * Dispatch action to hide/show and enable/disable the next button depending on the AI automation state and VPN connection.
   */
  useEffect(() => {
    if (showAIOptIn || vpnCheckWarning) {
      // Hide next button during AI opt-in disclosure, and while the VPN
      // status check warning offers Check again / Continue anyway instead
      dispatch({
        stepId: STEP_ID.CONNECT_VPN,
        type: ActionType.SET_HIDE_NEXT,
        hideNext: true,
      });
    } else {
      // Show next button for AI automation and manual flow
      dispatch({
        stepId: STEP_ID.CONNECT_VPN,
        type: ActionType.SET_HIDE_NEXT,
        hideNext: false,
      });

      if (aiAutomationInProgress || automationFailed) {
        // Keep button disabled during AI automation and when automation has failed
        dispatch({
          stepId: STEP_ID.CONNECT_VPN,
          type: ActionType.SET_NEXT_BTN_STATUS,
          enableNext: false,
        });
      } else {
        // Enable next button for manual flow
        dispatch({
          stepId: STEP_ID.CONNECT_VPN,
          type: ActionType.SET_NEXT_BTN_STATUS,
          enableNext: true,
        });
      }
    }
  }, [
    dispatch,
    showAIOptIn,
    aiAutomationInProgress,
    automationFailed,
    vpnCheckWarning,
  ]);

  /**
   * Renders the outcome of the on-demand VPN status check below the manual
   * setup steps: a soft warning with "Check again" / "Continue anyway"
   * actions when no connection was detected, or a success banner after a
   * retry confirms the connection. Renders nothing until a Next click has
   * run the check.
   */
  const renderVpnCheckBanner = (): JSX.Element | null => {
    if (vpnCheckPassedAfterRetry) {
      return (
        <>
          <Hr color="neutral20" size="narrow" />
          <MessageBanner
            dataTestId="vpnStatusCheckSuccessBanner"
            type={MessageBannerType.Success}
          >
            <Text>
              <FormattedMessage id="connectVpn-statusCheck-success" />
            </Text>
          </MessageBanner>
        </>
      );
    }

    if (!vpnCheckWarning) {
      return null;
    }

    return (
      <Col gridGap="S300" dataTestId="vpnStatusCheckWarning">
        <Hr color="neutral20" size="narrow" />
        {/* Per Stencil MessageBanner guidance (max 1 action inside a
            banner) and the RegisterSecurityKey timeout precedent, the
            banner carries only the message; actions sit below it. */}
        <MessageBanner
          dataTestId="vpnStatusCheckWarningBanner"
          type={MessageBannerType.Warning}
        >
          <Text>
            <FormattedMessage
              id="connectVpn-statusCheck-disconnected-warning"
              values={{ ciscoText: "Cisco Secure Client" }}
            />
          </Text>
        </MessageBanner>
        <Row justifyContent="space-between" alignItems="center">
          {/* Next is hidden while the warning shows: "Check again" takes
              over as the screen's single primary action, chat support
              appears beside it once a retry has failed (i.e. once we know
              they're actually stuck), and the "Continue anyway" bypass
              sits at the trailing edge where Next normally lives. */}
          <Row gridGap="S200" alignItems="center">
            <Button
              variant={ButtonVariant.Primary}
              onClick={() => void handleVpnCheckRetry()}
              disabled={vpnCheckInFlight}
              dataTestId="vpnStatusCheckRetryBtn"
            >
              {vpnCheckInFlight ? (
                <Spinner
                  colorScheme={SpinnerColorScheme.Inverted}
                  dataTestId="vpnStatusCheckRetrySpinner"
                />
              ) : (
                <FormattedMessage id="connectVpn-statusCheck-retry-button" />
              )}
            </Button>
            {vpnCheckRetryFailed && (
              <Button
                variant={ButtonVariant.Secondary}
                onClick={handleVpnCheckChatSupport}
                dataTestId="vpnStatusCheckChatSupportBtn"
              >
                <FormattedMessage id="text-chatWithITSupport" />
              </Button>
            )}
          </Row>
          <Button
            variant={ButtonVariant.Tertiary}
            onClick={handleVpnCheckContinueAnyway}
            dataTestId="vpnStatusCheckContinueAnywayBtn"
          >
            <FormattedMessage id="connectVpn-statusCheck-continueAnyway-button" />
          </Button>
        </Row>
      </Col>
    );
  };

  const renderContent = (): JSX.Element => {
    if (!isAiAutomationAvailable) {
      return (
        <>
          <VpnManualSetup />
          {renderVpnCheckBanner()}
        </>
      );
    }

    if (showAIOptIn) {
      return (
        <VpnOptIn
          onStartAutomation={handleStartAutomation}
          onDeclineAutomation={handleDeclineAutomation}
          hasUserPreviouslyConsented={hasUserPreviouslyConsented}
        />
      );
    }

    if (aiAutomationInProgress) {
      return (
        <VpnAutomationInProgress onStopAutomation={handleStopAutomation} />
      );
    }

    if (automationCompleted) {
      return <VpnAutomationSuccess />;
    }

    if (automationFailed) {
      return (
        <VpnAutomationError
          onBackToSetup={handleBackToSetup}
          onManualFallback={handleDeclineAutomation}
          failureCount={failureCount}
        />
      );
    }

    return (
      <>
        <VpnManualSetup />
        {renderVpnCheckBanner()}
      </>
    );
  };

  return <Col gridGap="S400">{renderContent()}</Col>;
}
